import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { earningsService } from '../services/api';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';

const EarningsAddPage = () => {
  const [rates, setRates] = useState({
    commission_rate: 0,
    tax_rate: 0,
    commission_waiting_days: 15,
    payment_working_days: 5
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statistics, setStatistics] = useState({});

  useEffect(() => {
    loadRates();
    loadStatistics();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      const response = await earningsService.getCommissionRates();
      if (response.success && response.data) {
        setRates({
          commission_rate: response.data.commission_rate?.value || 0,
          tax_rate: response.data.tax_rate?.value || 0,
          commission_waiting_days: response.data.commission_waiting_days?.value || 15,
          payment_working_days: response.data.payment_working_days?.value || 5
        });
      }
    } catch (error) {
      console.error('Error loading rates:', error);
      toast.error('Komisyon oranları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await earningsService.getAllEarningsForManagement({ limit: 1 });
      if (response.success && response.statistics) {
        setStatistics(response.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRates(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await earningsService.updateCommissionRates(rates);
      if (response.success) {
        toast.success('Komisyon oranları başarıyla güncellendi');
        loadStatistics(); // Refresh statistics
      } else {
        toast.error(response.error || 'Komisyon oranları güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating rates:', error);
      toast.error('Komisyon oranları güncellenirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="dashboard-main-content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <div className="dashboard-main-content">
        <div className="container-fluid">
          <Breadcrumb
            items={[
              { label: 'Dashboard', link: '/dashboard' },
              { label: 'Partnerler', link: '#' },
              { label: 'Hak Ediş Yönetimi', link: '/earnings/manage' },
              { label: 'Hak Ediş Ekle / Oranları Güncelle', link: '#' }
            ]}
          />

          <div className="row">
            {/* Statistics Cards */}
            <div className="col-12 mb-4">
              <div className="row">
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <Icon icon="solar:wallet-money-outline" className="text-primary" style={{ fontSize: '2rem' }} />
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-0 text-muted">Toplam Satış</h6>
                          <h4 className="mb-0">
                            {statistics.total_sales ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(statistics.total_sales) : '₺0'}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <Icon icon="solar:percent-circle-outline" className="text-success" style={{ fontSize: '2rem' }} />
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-0 text-muted">Toplam Komisyon</h6>
                          <h4 className="mb-0">
                            {statistics.total_commission ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(statistics.total_commission) : '₺0'}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <Icon icon="solar:chart-outline" className="text-info" style={{ fontSize: '2rem' }} />
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-0 text-muted">Ortalama Oran</h6>
                          <h4 className="mb-0">
                            {statistics.avg_commission_rate ? `${parseFloat(statistics.avg_commission_rate).toFixed(2)}%` : '0%'}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <Icon icon="solar:document-text-outline" className="text-warning" style={{ fontSize: '2rem' }} />
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-0 text-muted">Toplam Hak Ediş</h6>
                          <h4 className="mb-0">{statistics.total_count || 0}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Rates Form */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <Icon icon="solar:settings-outline" className="me-2" />
                    Komisyon Oranları ve Ayarlar
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="commission_rate" className="form-label">
                          Komisyon Oranı (%)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="commission_rate"
                          name="commission_rate"
                          value={rates.commission_rate}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          max="100"
                          required
                        />
                        <small className="form-text text-muted">
                          Varsayılan komisyon oranı (örn: 5.00 = %5)
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="tax_rate" className="form-label">
                          KDV Oranı (%)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="tax_rate"
                          name="tax_rate"
                          value={rates.tax_rate}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          max="100"
                          required
                        />
                        <small className="form-text text-muted">
                          KDV oranı (örn: 20.00 = %20)
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="commission_waiting_days" className="form-label">
                          Komisyon Bekleme Süresi (Gün)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="commission_waiting_days"
                          name="commission_waiting_days"
                          value={rates.commission_waiting_days}
                          onChange={handleChange}
                          min="1"
                          max="365"
                          required
                        />
                        <small className="form-text text-muted">
                          Satıştan sonra komisyonun kazanılması için beklenen gün sayısı (örn: 15)
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="payment_working_days" className="form-label">
                          Ödeme İş Günü Süresi
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="payment_working_days"
                          name="payment_working_days"
                          value={rates.payment_working_days}
                          onChange={handleChange}
                          min="1"
                          max="30"
                          required
                        />
                        <small className="form-text text-muted">
                          Fatura yüklendikten sonra ödeme için beklenen iş günü sayısı (örn: 5)
                        </small>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => loadRates()}
                      >
                        <Icon icon="solar:refresh-outline" className="me-2" />
                        Sıfırla
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Kaydediliyor...
                          </>
                        ) : (
                          <>
                            <Icon icon="solar:diskette-outline" className="me-2" />
                            Kaydet
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default EarningsAddPage;

